export type GPUBrand = 'nvidia' | 'amd' | 'intel';

export interface GPU {
  id: string;
  name: string;
  brand: GPUBrand;
  performance: number; // Percentage relative to RTX 4090
  vram: string;
  msrp: number;
  releaseYear: number;
  architecture: string;
  tdp: number;
}

export interface PerformanceTier {
  id: string;
  name: string;
  description: string;
  minPerformance: number;
  maxPerformance: number;
  color: 'ultra' | 'high' | 'mid' | 'entry';
  resolutionNote: string;
}

export const performanceTiers: PerformanceTier[] = [
  {
    id: 'ultra',
    name: '4K Ultra',
    description: 'Flagship performance for 4K gaming at ultra settings',
    minPerformance: 85,
    maxPerformance: 130,
    color: 'ultra',
    resolutionNote: 'Consistent 4K 60fps+ and 8K 30fps gaming',
  },
  {
    id: 'high',
    name: '4K High',
    description: 'Excellent 4K performance with high settings',
    minPerformance: 65,
    maxPerformance: 84,
    color: 'high',
    resolutionNote: 'Smooth 4K 60fps at high settings',
  },
  {
    id: 'mid',
    name: '1440p Ultra',
    description: 'Great performance for 1440p at ultra settings',
    minPerformance: 40,
    maxPerformance: 64,
    color: 'mid',
    resolutionNote: '1440p 60fps+ and 4K medium settings',
  },
  {
    id: 'entry',
    name: '1080p High',
    description: 'Solid 1080p gaming performance',
    minPerformance: 15,
    maxPerformance: 39,
    color: 'entry',
    resolutionNote: '1080p 60fps+ at high settings',
  },
];

export const gpuData: GPU[] = [
  // NVIDIA GPUs
  { id: 'rtx-5090', name: 'RTX 5090', brand: 'nvidia', performance: 125, vram: '32GB GDDR7', msrp: 1999, releaseYear: 2025, architecture: 'Blackwell', tdp: 575 },
  { id: 'rtx-4090', name: 'RTX 4090', brand: 'nvidia', performance: 100, vram: '24GB GDDR6X', msrp: 1599, releaseYear: 2022, architecture: 'Ada Lovelace', tdp: 450 },
  { id: 'rtx-5080', name: 'RTX 5080', brand: 'nvidia', performance: 95, vram: '16GB GDDR7', msrp: 999, releaseYear: 2025, architecture: 'Blackwell', tdp: 360 },
  { id: 'rtx-4080-super', name: 'RTX 4080 Super', brand: 'nvidia', performance: 90, vram: '16GB GDDR6X', msrp: 999, releaseYear: 2024, architecture: 'Ada Lovelace', tdp: 320 },
  { id: 'rtx-5070-ti', name: 'RTX 5070 Ti', brand: 'nvidia', performance: 90, vram: '16GB GDDR7', msrp: 749, releaseYear: 2025, architecture: 'Blackwell', tdp: 300 },
  { id: 'rtx-4080', name: 'RTX 4080', brand: 'nvidia', performance: 85, vram: '16GB GDDR6X', msrp: 1199, releaseYear: 2022, architecture: 'Ada Lovelace', tdp: 320 },
  { id: 'rtx-4070-ti-super', name: 'RTX 4070 Ti Super', brand: 'nvidia', performance: 75, vram: '16GB GDDR6X', msrp: 799, releaseYear: 2024, architecture: 'Ada Lovelace', tdp: 285 },
  { id: 'rtx-5070', name: 'RTX 5070', brand: 'nvidia', performance: 70, vram: '12GB GDDR7', msrp: 549, releaseYear: 2025, architecture: 'Blackwell', tdp: 250 },
  { id: 'rtx-4070-ti', name: 'RTX 4070 Ti', brand: 'nvidia', performance: 70, vram: '12GB GDDR6X', msrp: 799, releaseYear: 2023, architecture: 'Ada Lovelace', tdp: 285 },
  { id: 'rtx-4070-super', name: 'RTX 4070 Super', brand: 'nvidia', performance: 65, vram: '12GB GDDR6X', msrp: 599, releaseYear: 2024, architecture: 'Ada Lovelace', tdp: 220 },
  { id: 'rtx-4070', name: 'RTX 4070', brand: 'nvidia', performance: 55, vram: '12GB GDDR6X', msrp: 549, releaseYear: 2023, architecture: 'Ada Lovelace', tdp: 200 },
  { id: 'rtx-4060-ti', name: 'RTX 4060 Ti', brand: 'nvidia', performance: 45, vram: '8GB GDDR6', msrp: 399, releaseYear: 2023, architecture: 'Ada Lovelace', tdp: 160 },
  { id: 'rtx-4060', name: 'RTX 4060', brand: 'nvidia', performance: 35, vram: '8GB GDDR6', msrp: 299, releaseYear: 2023, architecture: 'Ada Lovelace', tdp: 115 },
  { id: 'rtx-3060', name: 'RTX 3060', brand: 'nvidia', performance: 30, vram: '12GB GDDR6', msrp: 329, releaseYear: 2021, architecture: 'Ampere', tdp: 170 },
  { id: 'rtx-3050', name: 'RTX 3050', brand: 'nvidia', performance: 20, vram: '8GB GDDR6', msrp: 249, releaseYear: 2022, architecture: 'Ampere', tdp: 130 },

  // AMD GPUs
  { id: 'rx-7900-xtx', name: 'RX 7900 XTX', brand: 'amd', performance: 90, vram: '24GB GDDR6', msrp: 999, releaseYear: 2022, architecture: 'RDNA 3', tdp: 355 },
  { id: 'rx-9070-xt', name: 'RX 9070 XT', brand: 'amd', performance: 90, vram: '16GB GDDR6', msrp: 649, releaseYear: 2025, architecture: 'RDNA 4', tdp: 304 },
  { id: 'rx-7900-xt', name: 'RX 7900 XT', brand: 'amd', performance: 80, vram: '20GB GDDR6', msrp: 899, releaseYear: 2022, architecture: 'RDNA 3', tdp: 315 },
  { id: 'rx-9070', name: 'RX 9070', brand: 'amd', performance: 75, vram: '16GB GDDR6', msrp: 549, releaseYear: 2025, architecture: 'RDNA 4', tdp: 220 },
  { id: 'rx-7900-gre', name: 'RX 7900 GRE', brand: 'amd', performance: 70, vram: '16GB GDDR6', msrp: 549, releaseYear: 2024, architecture: 'RDNA 3', tdp: 260 },
  { id: 'rx-7800-xt', name: 'RX 7800 XT', brand: 'amd', performance: 60, vram: '16GB GDDR6', msrp: 499, releaseYear: 2023, architecture: 'RDNA 3', tdp: 263 },
  { id: 'rx-7700-xt', name: 'RX 7700 XT', brand: 'amd', performance: 50, vram: '12GB GDDR6', msrp: 449, releaseYear: 2023, architecture: 'RDNA 3', tdp: 245 },
  { id: 'rx-7600-xt', name: 'RX 7600 XT', brand: 'amd', performance: 40, vram: '16GB GDDR6', msrp: 329, releaseYear: 2024, architecture: 'RDNA 3', tdp: 190 },
  { id: 'rx-7600', name: 'RX 7600', brand: 'amd', performance: 35, vram: '8GB GDDR6', msrp: 269, releaseYear: 2023, architecture: 'RDNA 3', tdp: 165 },
  { id: 'rx-6700-xt', name: 'RX 6700 XT', brand: 'amd', performance: 30, vram: '12GB GDDR6', msrp: 479, releaseYear: 2021, architecture: 'RDNA 2', tdp: 230 },
  { id: 'rx-6600', name: 'RX 6600', brand: 'amd', performance: 25, vram: '8GB GDDR6', msrp: 329, releaseYear: 2021, architecture: 'RDNA 2', tdp: 132 },

  // Intel GPUs
  { id: 'arc-b580', name: 'Arc B580', brand: 'intel', performance: 40, vram: '12GB GDDR6', msrp: 249, releaseYear: 2024, architecture: 'Battlemage', tdp: 190 },
  { id: 'arc-a770', name: 'Arc A770', brand: 'intel', performance: 35, vram: '16GB GDDR6', msrp: 349, releaseYear: 2022, architecture: 'Alchemist', tdp: 225 },
  { id: 'arc-a750', name: 'Arc A750', brand: 'intel', performance: 30, vram: '8GB GDDR6', msrp: 289, releaseYear: 2022, architecture: 'Alchemist', tdp: 225 },
];

export function getGPUsByTier(tier: PerformanceTier): GPU[] {
  return gpuData.filter(
    gpu => gpu.performance >= tier.minPerformance && gpu.performance <= tier.maxPerformance
  );
}

export function getGPUsByBrand(brand: GPUBrand): GPU[] {
  return gpuData.filter(gpu => gpu.brand === brand);
}

export function getTierForPerformance(performance: number): PerformanceTier | undefined {
  return performanceTiers.find(
    tier => performance >= tier.minPerformance && performance <= tier.maxPerformance
  );
}

export function sortGPUsByPerformance(gpus: GPU[], ascending = false): GPU[] {
  return [...gpus].sort((a, b) => 
    ascending ? a.performance - b.performance : b.performance - a.performance
  );
}
